import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import { RootState } from "@/app/rootReducer";
import {
  fetchAdminUsers,
  FetchAdminUsersRequest,
  FetchAdminUsersResponse,
  AdminUser,
} from "../api/fetchAdminUsers";

export type { AdminUser } from "../api/fetchAdminUsers";

export interface RemoteSyncLog {
  id: number;
  status: "pending" | "in_progress" | "success" | "failed";
  log: string;
  syncResults: {
    salesforce?: { request: unknown; response: unknown };
    memberportal?: { request: unknown; response: unknown };
    partnernetwork?: { request: unknown; response: unknown };
    scoreboard?: { request: unknown; response: unknown };
    craft?: { request: unknown; response: unknown };
  } | null;
  startedAt: string | null;
  completedAt: string | null;
}

interface AdminUsersState {
  users: AdminUser[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  syncingEmployeeIds: number[]; // Employees currently being synced (for real-time updates)
}

const initialState: AdminUsersState = {
  users: [],
  totalCount: 0,
  loading: false,
  error: null,
  syncingEmployeeIds: [],
};

const adminUsersSlice = createSlice({
  name: "adminUsers",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setUsersData: (
      state,
      action: PayloadAction<{ users: AdminUser[]; totalCount: number }>,
    ) => {
      state.users = action.payload.users;
      state.totalCount = action.payload.totalCount;
    },
    // Real-time sync tracking
    addSyncingEmployee: (state, action: PayloadAction<number>) => {
      if (!state.syncingEmployeeIds.includes(action.payload)) {
        state.syncingEmployeeIds.push(action.payload);
      }
    },
    removeSyncingEmployee: (state, action: PayloadAction<number>) => {
      state.syncingEmployeeIds = state.syncingEmployeeIds.filter(
        (id) => id !== action.payload,
      );
    },
    updateUserSyncLog: (
      state,
      action: PayloadAction<{ employeeId: number; syncLog: RemoteSyncLog }>,
    ) => {
      const { employeeId, syncLog } = action.payload;
      const userIndex = state.users.findIndex(
        (u) => u.employeeId === employeeId,
      );
      if (userIndex !== -1) {
        state.users[userIndex].remoteSyncLog = syncLog;
      }
    },
  },
});

export const {
  setLoading,
  setError,
  setUsersData,
  addSyncingEmployee,
  removeSyncingEmployee,
  updateUserSyncLog,
} = adminUsersSlice.actions;

export const selectAdminUsers = (state: RootState) => state.adminUsers.users;
export const selectAdminUsersLoading = (state: RootState) =>
  state.adminUsers.loading;
export const selectAdminUsersError = (state: RootState) =>
  state.adminUsers.error;
export const selectAdminUsersTotalCount = (state: RootState) =>
  state.adminUsers.totalCount;
export const selectSyncingEmployeeIds = (state: RootState) =>
  state.adminUsers.syncingEmployeeIds;

export const fetchAdminUsersAction =
  (requestData: FetchAdminUsersRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const response: FetchAdminUsersResponse =
        await fetchAdminUsers(requestData);
      dispatch(
        setUsersData({
          users: response.data.users,
          totalCount: response.meta?.totalCount || 0,
        }),
      );
    } catch (error) {
      dispatch(
        setError(
          error instanceof Error ? error.message : "Failed to fetch users",
        ),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

export default adminUsersSlice.reducer;
